import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import ReadingProgress from './components/ReadingProgress';
import { usePageMeta } from '@/hooks/usePageMeta';
import { articles } from '@/mocks/articles';
import type { Article } from '@/types/content';

const all = articles as Article[];

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = all.find((a) => a.id === id);
  const index = all.findIndex((a) => a.id === id);
  const prev = index > 0 ? all[index - 1] : null;
  const next = index >= 0 && index < all.length - 1 ? all[index + 1] : null;

  const related = useMemo(() => {
    if (!article) return [];
    const sameCat = all.filter((a) => a.id !== article.id && a.category === article.category);
    const others = all.filter((a) => a.id !== article.id && a.category !== article.category);
    return [...sameCat, ...others].slice(0, 3);
  }, [article]);

  const schema = useMemo(() => {
    if (!article) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      articleSection: article.categoryLabel,
      keywords: article.tags.join(','),
      author: { '@type': 'Organization', name: article.author },
      publisher: { '@type': 'Organization', name: '吃瓜福利社' },
      mainEntityOfPage: `https://coulex.cn/article/${article.id}`,
    };
  }, [article]);

  usePageMeta({
    title: article ? `${article.title} - 福利吃瓜` : '内容不存在 - 福利吃瓜',
    description: article
      ? `${article.excerpt}（福利吃瓜 · ${article.categoryLabel}）`
      : '该内容可能已下架或链接有误，欢迎返回瓜料库浏览其他档期内容。',
    keywords: article ? `福利吃瓜,${article.tags.join(',')},${article.categoryLabel}` : '福利吃瓜',
    path: article ? `/article/${article.id}` : '/topics',
    schema,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-16 md:pt-20">
          <div className="w-full px-4 md:px-8 lg:px-12 py-24 text-center">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-400 items-center justify-center">
              <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">找不到这篇瓜料</h1>
            <p className="mt-3 text-sm text-foreground-600">链接可能已经失效，回瓜料库看看其他档期内容吧。</p>
            <Link
              to="/topics"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回瓜料库
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const lead = article.content[0];
  const mid = article.content.slice(1, 4);
  const rest = article.content.slice(4);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <ReadingProgress />
      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-8 border-b border-background-200">
          <Breadcrumbs
            items={[
              { label: '瓜料库', to: '/topics' },
              { label: article.categoryLabel, to: `/topics?cat=${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.categoryLabel}
              </span>
              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug md:leading-tight">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" aria-hidden="true"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5 text-primary-600 font-medium">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  {article.heat} 热度
                </span>
              </div>

              <div className="mt-7 w-full h-[280px] md:h-[420px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 福利吃瓜${article.categoryLabel}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 md:mt-10 max-w-3xl">
                <p className="text-base md:text-lg text-foreground-900 leading-8 font-medium">{lead}</p>

                {mid.length > 0 && (
                  <div className="article-body mt-8">
                    <h2>这条瓜为什么值得追</h2>
                    {mid.map((p, i) => (
                      <p key={`mid-${i}`}>{p}</p>
                    ))}
                  </div>
                )}

                {rest.length > 0 && (
                  <div className="article-body mt-8">
                    <h2>值得留意的细节</h2>
                    {rest.map((p, i) => (
                      <p key={`rest-${i}`}>{p}</p>
                    ))}
                  </div>
                )}

                <div className="mt-8 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 mr-1">相关标签：</span>
                  {article.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/topics?q=${encodeURIComponent(t)}`}
                      className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                    >
                      #{t}
                    </Link>
                  ))}
                </div>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {prev ? (
                    <Link
                      to={`/article/${prev.id}`}
                      className="group rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
                    >
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-arrow-left-line" aria-hidden="true"></i>
                        上一篇
                      </span>
                      <span className="mt-2 block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {prev.title}
                      </span>
                    </Link>
                  ) : (
                    <div className="rounded-lg border border-dashed border-background-300 p-5 flex items-center">
                      <span className="text-xs text-foreground-400">已经是最新一篇</span>
                    </div>
                  )}
                  {next ? (
                    <Link
                      to={`/article/${next.id}`}
                      className="group rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors sm:text-right"
                    >
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5 sm:justify-end">
                        下一篇
                        <i className="ri-arrow-right-line" aria-hidden="true"></i>
                      </span>
                      <span className="mt-2 block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {next.title}
                      </span>
                    </Link>
                  ) : (
                    <div className="rounded-lg border border-dashed border-background-300 p-5 flex items-center sm:justify-end">
                      <span className="text-xs text-foreground-400">已经是最早一篇</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-4 lg:sticky lg:top-28 h-fit space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-book-open-line text-primary-500" aria-hidden="true"></i>
                  内容速览
                </h2>
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">所属档期</dt>
                    <dd className="text-foreground-900 font-medium">{article.categoryLabel}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">发布时间</dt>
                    <dd className="text-foreground-900">{article.date}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">阅读时长</dt>
                    <dd className="text-foreground-900">{article.readTime}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">讨论热度</dt>
                    <dd className="text-primary-600 font-bold">{article.heat}</dd>
                  </div>
                </dl>
                <Link
                  to={`/topics?cat=${article.category}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  进入「{article.categoryLabel}」栏目
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-links-line text-primary-500" aria-hidden="true"></i>
                  相关推荐
                </h2>
                <div className="space-y-1">
                  {related.map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      className="group block py-3 border-b border-background-200 last:border-0"
                    >
                      <span className="block text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {a.title}
                      </span>
                      <span className="mt-1.5 block text-[11px] text-foreground-500">
                        {a.categoryLabel} · {a.readTime}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-foreground-950 p-6">
                <h2 className="font-heading text-base font-bold text-background-50 mb-2">不错过任何一场好戏</h2>
                <p className="text-sm text-background-300 leading-7">
                  订阅档期提醒，上新与换挡时第一时间通知你。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  去订阅
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </aside>
          </div>
        </article>

        <section className="py-12 md:py-16 bg-background-100 border-t border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="flex items-end justify-between gap-4 mb-8">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">继续看相关瓜料</h2>
              <Link
                to="/topics"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap transition-colors"
              >
                全部内容
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}